import React, {ReactNode} from "react";
import ReactMarkdown, {Components} from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, {defaultSchema} from "rehype-sanitize";
import {Box, Link} from "@mui/material";
import {alpha, Theme} from "@mui/material/styles";
import {Link as RouterLink} from "react-router-dom";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

/** Texto plano de los hijos de un nodo React (para ids de título y para detectar notas). */
export const textOf = (node: ReactNode): string => {
	if (node === null || node === undefined || typeof node === "boolean") return "";
	if (typeof node === "string" || typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(textOf).join("");
	if (React.isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children);
	return "";
};

export const slugify = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "")
	.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "seccion";

// El HTML que se escriba en una página se pinta para todos los empleados: se admite el habitual
// de Markdown y nada ejecutable (sin <script>, sin on*, sin style, sin javascript:). Ver #14119.
const sanitizeSchema = {
	...defaultSchema,
	attributes: {
		...defaultSchema.attributes,
		code: [...(defaultSchema.attributes?.code || []), ["className", /^language-./]],
	},
};

const WARNING_PREFIX = /^(atención|importante|aviso|cuidado)\b/i;

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** Estilo de lectura común a la wiki y a su vista previa en el editor. */
export const proseSx = (theme: Theme) => ({
	fontSize: 13.5,
	lineHeight: 1.65,
	color: "text.primary",
	overflowWrap: "anywhere" as const,
	"& .prose-first": {mt: 0, pt: 0, borderTop: 0},
	"& h1, & h2, & h3, & h4": {fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.01em", scrollMarginTop: 96},
	"& h1": {fontSize: "1.3rem", mt: 3.5, mb: 1.25},
	"& h2": {fontSize: "1.08rem", mt: 4, mb: 1, pt: 2.25, borderTop: "1px solid", borderColor: "divider"},
	"& h3": {fontSize: "0.95rem", mt: 2.75, mb: 0.75},
	"& h4": {fontSize: "0.9rem", mt: 2.25, mb: 0.5},
	"& p": {my: 1.25},
	"& ul, & ol": {pl: 3, my: 1.25},
	"& li": {my: 0.35, pl: 0.5},
	"& li::marker": {color: "text.secondary"},
	"& li > ul, & li > ol": {my: 0.5},
	"& strong": {fontWeight: 700},
	"& hr": {border: 0, borderTop: "1px solid", borderColor: "divider", my: 4},
	"& img": {maxWidth: "100%", height: "auto", borderRadius: 2, display: "block", my: 2},
	"& :not(pre) > code": {
		fontFamily: MONO, fontSize: "0.88em", bgcolor: alpha(theme.palette.primary.main, 0.07),
		color: "text.primary", px: 0.6, py: 0.15, borderRadius: 1,
	},
	"& pre": {
		fontFamily: MONO, fontSize: 12, lineHeight: 1.6, bgcolor: "grey.50", border: "1px solid",
		borderColor: "divider", p: 2, borderRadius: 2, overflowX: "auto", my: 2,
	},
	"& pre code": {fontFamily: "inherit"},
	"& .prose-table": {overflowX: "auto", my: 2, border: "1px solid", borderColor: "divider", borderRadius: 2},
	"& table": {borderCollapse: "collapse", width: "100%", fontSize: 12.75},
	"& th": {bgcolor: "grey.50", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap"},
	"& th, & td": {px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider", verticalAlign: "top"},
	"& tr:last-of-type td": {borderBottom: 0},
	"& td:has(> input[type=checkbox])": {width: 32},
	"& input[type=checkbox]": {mr: 1, transform: "translateY(1px)"},
	"& .contains-task-list": {listStyle: "none", pl: 0.5},
	"& .task-list-item": {pl: 0},
});

const Note = ({children}: { children: ReactNode }) => {
	const warning = WARNING_PREFIX.test(textOf(children).trim());
	return (
		<Box component="aside" sx={theme => {
			const c = warning ? theme.palette.warning.main : theme.palette.info.main;
			return {
				display: "flex", gap: 1.5, my: 2, px: 2, py: 1.5, borderRadius: 2,
				bgcolor: alpha(c, 0.08), border: "1px solid", borderColor: alpha(c, 0.25),
				"& > svg": {color: warning ? "warning.dark" : "info.dark", mt: 0.4, flexShrink: 0},
				"& p": {my: 0.5},
			};
		}}>
			{warning ? <WarningAmberRoundedIcon fontSize="small"/> : <InfoOutlinedIcon fontSize="small"/>}
			<Box sx={{minWidth: 0}}>{children}</Box>
		</Box>
	);
};

const SmartLink = ({href, children}: { href?: string, children?: ReactNode }) => {
	if (!href) return <>{children}</>;
	if (href.startsWith("/")) {
		return <Link component={RouterLink} to={href} underline="hover" fontWeight={600}>{children}</Link>;
	}
	const external = /^https?:\/\//i.test(href);
	return (
		<Link href={href} underline="hover" fontWeight={600}
			  {...(external ? {target: "_blank", rel: "noopener noreferrer"} : {})}>
			{children}
			{external && <OpenInNewRoundedIcon sx={{fontSize: "0.85em", ml: 0.3, verticalAlign: "-0.1em", opacity: 0.7}}/>}
		</Link>
	);
};

const buildComponents = (): Components => {
	const used = new Map<string, number>();
	// Sin :first-child en el CSS (Emotion lo desaconseja): el título que abre la página lo marca su posición.
	const heading = (Tag: "h1" | "h2" | "h3" | "h4") => ({children, node}: { children?: ReactNode, node?: { position?: { start?: { line?: number } } } }) => {
		const base = slugify(textOf(children));
		const n = (used.get(base) || 0) + 1;
		used.set(base, n);
		const first = (node?.position?.start?.line ?? 0) <= 1;
		return <Tag id={n > 1 ? `${base}-${n}` : base} className={first ? "prose-first" : undefined}>{children}</Tag>;
	};
	return {
		h1: heading("h1"),
		h2: heading("h2"),
		h3: heading("h3"),
		h4: heading("h4"),
		a: ({href, children}) => <SmartLink href={href}>{children}</SmartLink>,
		blockquote: ({children}) => <Note>{children}</Note>,
		table: ({children}) => <div className="prose-table"><table>{children}</table></div>,
		img: ({src, alt}) => <img src={src} alt={alt || ""} loading="lazy"/>,
	};
};

interface ProseProps {
	content: string;
}

/** Contenido de una página de la wiki, igual en lectura y en la vista previa del editor. */
export const Prose = React.forwardRef<HTMLDivElement, ProseProps>(({content}, ref) => (
	<Box ref={ref} sx={proseSx}>
		<ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
					   components={buildComponents()}>{content}</ReactMarkdown>
	</Box>
));
Prose.displayName = "Prose";
